import React, { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useSnackbar } from "notistack";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import CandidateTable from "./candidate/CandidateTable";
import sh from "bundles/common/utils/sh";
import NotesDrawer from "bundles/Internal/components/common/NotesDrawer";
import { useCandidateModal } from "bundles/Internal/components/common/CandidateModalProvider";
import { buildSearchFilters } from "bundles/Internal/components/CandidatesIndexPage";
import { EMPTY_FILTERS } from "bundles/Internal/components/candidate/CandidateTableFilters";
import { appendFormValue } from "bundles/Internal/utils/candidateFormData";

const ArchivedCandidatesPage = () => {
  const [candidates, setCandidates] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const { openCreateCandidate } = useCandidateModal();
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesCandidate, setNotesCandidate] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);

  const fetchCandidates = useCallback((query = "", pageNum = 1, limit = 25, activeFilters = {}) => {
    const searchFilters = buildSearchFilters(activeFilters);

    setLoading(true);
    sh.get("/internal/candidates", {
      params: {
        scope: "archived",
        query: String(query || "").trim() || undefined,
        page: pageNum,
        limit,
        ...(searchFilters && { filters: JSON.stringify(searchFilters) }),
      },
    })
      .then((res) => {
        const payload = res.data?.data || [];
        setCandidates(payload);
        setTotalCount(res.data?.pagy?.count ?? payload.length);
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || String(err?.message || "").toLowerCase().includes("canceled")) return;
        enqueueSnackbar("Failed to fetch archived candidates", { variant: "error" });
      })
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchCandidates(searchQuery, 1, rowsPerPage, filters);
    }, 250);
    return () => clearTimeout(t);
  }, [fetchCandidates, searchQuery, rowsPerPage, filters]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    fetchCandidates(searchQuery, newPage, rowsPerPage, filters);
  }, [fetchCandidates, searchQuery, rowsPerPage, filters]);

  const handleRowsPerPageChange = useCallback((newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  const handleCreateSuccess = (reset, close) => {
    enqueueSnackbar("Candidate created", { variant: "success" });
    reset?.();
    close?.();
    fetchCandidates(searchQuery, page, rowsPerPage, filters);
  };

  const handleCreateError = (err) => {
    const messages = err?.response?.data?.errors;
    if (err?.response?.status === 422 && Array.isArray(messages) && messages.length > 0) {
      messages.forEach((msg) => enqueueSnackbar(msg, { variant: "error" }));
      return;
    }
    enqueueSnackbar("Failed to create candidate", { variant: "error" });
  };

  const createCandidate = (values, { reset, resumeFile, autoEmail, close } = {}) => {
    if (!resumeFile) {
      return sh.post("/internal/candidates", {
        candidate: values,
        authenticity_token,
        auto_email: autoEmail,
      })
        .then(() => handleCreateSuccess(reset, close))
        .catch(handleCreateError);
    }

    const formData = new FormData();
    formData.append("authenticity_token", authenticity_token);

    const appendValue = (key, value) => appendFormValue(formData, key, value);

    Object.entries(values || {}).forEach(([k, v]) => appendValue(`candidate[${k}]`, v));
    formData.append("candidate[resume]", resumeFile);

    if (autoEmail && typeof autoEmail === "object") {
      Object.entries(autoEmail).forEach(([k, v]) => appendValue(`auto_email[${k}]`, v));
    }

    return sh.post("/internal/candidates", formData)
      .then(() => handleCreateSuccess(reset, close))
      .catch(handleCreateError);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #F9FAFF 0%, #EEF0F6 100%)",
      }}
    >
      <CandidateTable
        candidates={candidates}
        onOpenCreateCandidate={() => openCreateCandidate({ onSubmit: createCandidate })}
        onQueryChange={(q) => setSearchQuery(q)}
        filters={filters}
        onFiltersChange={setFilters}
        onOpenNotes={(candidate) => {
          setNotesCandidate(candidate);
          setNotesOpen(true);
        }}
        onCandidateSelect={(candidate) => {
          if (!candidate?.id) return;
          navigate(`/internal/candidates/${candidate.id}`, { state: { candidate, from: location.pathname } });
        }}
        page={page}
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        loading={loading}
      />


      <NotesDrawer
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        resource="candidates"
        record={notesCandidate}
        onNoteAdded={(candidateId) => {
          setCandidates((prev) =>
            prev.map((c) =>
              String(c?.id) === String(candidateId)
                ? { ...c, notes_count: (Number(c.notes_count) || 0) + 1 }
                : c
            )
          );
        }}
        onNoteArchived={(candidateId) => {
          setCandidates((prev) =>
            prev.map((c) =>
              String(c?.id) === String(candidateId)
                ? { ...c, notes_count: Math.max((Number(c.notes_count) || 0) - 1, 0) }
                : c
            )
          );
        }}
      />
    </Box>
  );
};

export default ArchivedCandidatesPage;
